import React from "react";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const CSRD = () => {
  // No special scroll behavior needed
  
  return (
    <div className="relative min-h-screen">
      <Helmet>
        <title>CSRD Compliance | Verdea AI</title>
        <meta name="description" content="Corporate Sustainability Reporting Directive compliance solutions powered by AI." />
      </Helmet>
      
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 right-0 w-96 h-96 blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.4) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(30%, -30%)'
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-96 h-96 blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(-30%, 30%)'
          }}
        ></div>
      </div>
      
      {/* Navbar */}
      <header className="sticky top-0 z-50">
        <NavBar />
      </header>
      
      <main>
        {/* Hero section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  CSRD Compliance
                  <span className="block text-2xl md:text-3xl text-emerald-600 mt-2">
                    Simplify Corporate Sustainability Reporting
                  </span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                  Our AI-powered platform streamlines CSRD compliance, helping businesses transform sustainability data into accurate, ESRS-compliant reports.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Request Demo
                  </Button>
                  <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                    Learn More
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        
        {/* Main content section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-semibold mb-6">CSRD Made Simple</h2>
                  <p className="text-lg text-gray-600 mb-6">
                    The Corporate Sustainability Reporting Directive (CSRD) introduces extensive reporting requirements for companies operating in the EU. Our platform transforms complex ESRS standards into manageable workflows.
                  </p>
                  <ul className="space-y-3">
                    {[
                      "Cover all 12 ESRS standards with comprehensive data collection",
                      "AI-powered data extraction from existing documents",
                      "Automated gap analysis against CSRD requirements",
                      "Dynamic materiality assessment tools",
                      "Fully auditable reporting process with version control"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-emerald-500 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg p-1 overflow-hidden"
                >
                  <div className="aspect-video bg-gray-100 rounded-lg">
                    <img 
                      src="/images/frameworks/csrd.svg" 
                      alt="CSRD Dashboard" 
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                </motion.div>
              </div>
              
              {/* Key features */}
              <div className="mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center mb-12"
                >
                  <h2 className="text-3xl font-semibold mb-4">Key CSRD Features</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Our platform addresses all aspects of CSRD compliance with sophisticated AI tools and intuitive workflows.
                  </p>
                </motion.div>
                
                <div className="grid md:grid-cols-3 gap-8">
                  {[
                    {
                      title: "Materiality Assessment",
                      description: "AI-assisted double materiality assessment to identify and prioritize sustainability issues most relevant to your business and stakeholders.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      )
                    },
                    {
                      title: "ESRS-Aligned Data Collection",
                      description: "Structured data collection forms and workflows aligned with all European Sustainability Reporting Standards (ESRS).",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                      )
                    },
                    {
                      title: "Document AI",
                      description: "Advanced AI extraction of sustainability metrics from existing reports, policies, and unstructured data sources.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )
                    },
                    {
                      title: "Gap Analysis",
                      description: "Comprehensive gap assessment between your current sustainability data and CSRD requirements with actionable recommendations.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                      )
                    },
                    {
                      title: "XBRL Tagging",
                      description: "Automated XBRL tagging of sustainability data for digital reporting compliance with European Single Electronic Format (ESEF).",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                      )
                    },
                    {
                      title: "Audit Trail",
                      description: "Complete audit trail and version control for all sustainability data and calculations to ensure transparency and assurance readiness.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      )
                    }
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                    >
                      {feature.icon}
                      <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* ESRS Coverage section */}
              <div className="bg-white rounded-xl shadow-lg p-10 mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center mb-10"
                >
                  <h2 className="text-3xl font-semibold mb-4">Complete ESRS Coverage</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Our platform provides comprehensive coverage of all European Sustainability Reporting Standards.
                  </p>
                </motion.div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    "ESRS 1: General Requirements",
                    "ESRS 2: General Disclosures",
                    "ESRS E1: Climate Change",
                    "ESRS E2: Pollution",
                    "ESRS E3: Water & Marine Resources",
                    "ESRS E4: Biodiversity & Ecosystems",
                    "ESRS E5: Resource Use & Circular Economy",
                    "ESRS S1: Own Workforce",
                    "ESRS S2: Workers in the Value Chain",
                    "ESRS S3: Affected Communities",
                    "ESRS S4: Consumers & End-users",
                    "ESRS G1: Business Conduct"
                  ].map((standard, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      viewport={{ once: true }}
                      className="bg-gray-50 p-4 rounded-lg flex items-center"
                    >
                      <svg className="w-5 h-5 text-emerald-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">{standard}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* Benefits section */}
              <div className="mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center mb-10"
                >
                  <h2 className="text-3xl font-semibold mb-4">Benefits of Our CSRD Solution</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Our platform delivers measurable improvements to your sustainability reporting process.
                  </p>
                </motion.div>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {[
                    {
                      title: "75% Time Savings",
                      description: "Reduce time spent on CSRD compliance and reporting by up to 75% through automation and AI assistance."
                    },
                    {
                      title: "95% Data Accuracy",
                      description: "Our AI validation ensures high accuracy in sustainability data collection and reporting."
                    },
                    {
                      title: "50% Cost Reduction",
                      description: "Significantly reduce compliance costs by streamlining processes and reducing dependency on external consultants."
                    },
                    {
                      title: "Full Assurance Readiness",
                      description: "Be fully prepared for third-party assurance with comprehensive audit trails and data lineage."
                    }
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex"
                    >
                      <div className="mr-4 flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                          <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* CTA section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl shadow-lg p-10 text-white text-center"
              >
                <h2 className="text-3xl font-semibold mb-4">Ready to Simplify Your CSRD Compliance Journey?</h2>
                <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
                  Join forward-thinking companies who are streamlining their sustainability reporting with our AI-powered platform.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100">
                    Schedule a Demo
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-700">
                    Contact Sales
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default CSRD;